// Fabric Workload entry point.
//
// The Fabric portal loads this iframe in either UI mode (visible) or
// Worker mode (invisible). The @ms-fabric/workload-client `bootstrap()`
// helper detects which mode and routes to the correct initializer.
//
// Reference:
//   https://learn.microsoft.com/javascript/api/@ms-fabric/workload-client
//
// STANDALONE DEV MODE (v0.8.0 — for visual smoke-testing migrations):
// When opened directly in a browser (i.e., not inside Fabric's iframe),
// `bootstrap()` waits forever for a Fabric host postMessage that never
// arrives → blank page. To allow `http://localhost:60007/` to render
// during local development, we detect "not in iframe" (window.top ===
// window) and skip bootstrap, invoking initializeUI() directly with
// empty params. API calls (OneLake, Fabric REST) will fail in this
// mode, but the UI renders so you can verify Fabric UX styling.
// To force the iframe path (for DevGateway / Fabric testing), append
// `?force-fabric` to the URL.
import { bootstrap, type InitParams } from "@ms-fabric/workload-client";

// MSAL/redirect-style auth flows often round-trip through "/close" to
// signal the popup window to close itself. The Fabric SDK uses the same
// pattern. Bail out before bootstrap runs.
const url = new URL(window.location.href);
if (url.pathname?.startsWith("/close")) {
  window.close();
}

const isStandalone =
  window.top === window.self && !url.searchParams.has("force-fabric");

if (isStandalone) {
  // Mount UI directly — bypass the Fabric host handshake.
  // eslint-disable-next-line no-console
  console.warn(
    "[workload-v2] Running in STANDALONE dev mode (no Fabric host). " +
      "API calls will fail; UI is rendered for visual verification only. " +
      "Append ?force-fabric to the URL to use the real bootstrap path."
  );
  void import("./index.ui").then(({ initialize }) =>
    initialize({} as InitParams)
  );
} else {
  bootstrap({
    initializeUI: (params) =>
      import("./index.ui").then(({ initialize }) => initialize(params)),
    initializeWorker: (params) =>
      import("./index.worker").then(({ initialize }) => initialize(params)),
  });
}

